import * as XLSX from 'xlsx';
import { supabase } from '../supabase';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { EXCLUDE_REFERENTI } from '../utils/contactPollution';
import { regionDisplayName } from '../utils/italianRegions';

// Batch size for .in() queries to avoid PostgREST URL length limits
const IN_BATCH_SIZE = 200;

/**
 * Execute a query with .in() filter in batches to avoid URL length limits.
 * Returns the concatenated results from all batches.
 */
async function queryInBatches<T>(
  ids: string[],
  queryFn: (batchIds: string[]) => Promise<{ data: T[] | null; error: any }>
): Promise<T[]> {
  if (ids.length === 0) return [];
  const results: T[] = [];
  for (let i = 0; i < ids.length; i += IN_BATCH_SIZE) {
    const batch = ids.slice(i, i + IN_BATCH_SIZE);
    const { data, error } = await queryFn(batch);
    if (error) throw error;
    if (data) results.push(...data);
  }
  return results;
}

/**
 * Fetch all pages from a Supabase query that may exceed the server max-rows limit (1000).
 * Paginates automatically until all rows are retrieved.
 */
async function fetchAllPages<T>(
  buildQuery: () => any
): Promise<T[]> {
  const PAGE_SIZE = 1000;
  const allData: T[] = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) throw error;
    if (data) allData.push(...data);
    hasMore = data?.length === PAGE_SIZE;
    page++;
  }
  return allData;
}

export interface ExportOptions {
  filters?: Record<string, any>;
  includeColumns?: string[];
  dateRange?: {
    from: string;
    to: string;
  };
}

interface ExportData {
  data: any[];
  filename: string;
  sheetName: string;
}

class ExportService {
  /**
   * Esporta clienti in Excel
   */
  async exportClients(options: ExportOptions = {}): Promise<void> {
    try {
      console.log('🚀 [EXPORT CLIENTS DEBUG] Starting export with options:', JSON.stringify(options, null, 2));

      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Simplified query without complex joins that might fail
      // We'll fetch related data separately for better error handling
      let query = supabase
        .from('clients')
        .select(`
          id,
          business_name,
          vat_number,
          tax_code,
          email,
          phone,
          street_address,
          street_number,
          city,
          postal_code,
          province,
          region,
          country_code,
          stato_cliente,
          lead_source,
          lead_stage,
          client_category,
          entity_type,
          created_at,
          updated_at,
          sales_rep_id,
          referrer_id,
          partner_id,
          assigned_to,
          newsletter_subscribed,
          newsletter_email
        `)
        .eq('organization_id', userProfile.organization_id)
        .or(EXCLUDE_REFERENTI); // Fase 2: no persone-referente nell'export CSV

      // Applica filtri se presenti
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            // Gestione filtri speciali
            switch (key) {
              case 'selected_tags':
                // Filtro per tag - deve avere TUTTI i tag selezionati
                if (Array.isArray(value) && value.length > 0) {
                  // Questo richiede una query più complessa che sarà gestita più avanti
                }
                break;
              case 'show_missing_tax_id':
                if (value === true) {
                  query = query.or('vat_number.is.null,tax_code.is.null');
                }
                break;
              case 'selected_service':
                // Filtro per servizio - gestito tramite join con deals
                // Verrà implementato nella logica di post-processing
                break;
              case 'search_query':
                // Filtro di ricerca su più campi
                if (typeof value === 'string' && value.trim()) {
                  const searchTerm = value.toLowerCase();
                  query = query.or(
                    `business_name.ilike.%${searchTerm}%,` +
                    `trade_name.ilike.%${searchTerm}%,` +
                    `email.ilike.%${searchTerm}%,` +
                    `vat_number.ilike.%${searchTerm}%,` +
                    `tax_code.ilike.%${searchTerm}%,` +
                    `phone.ilike.%${searchTerm}%`
                  );
                }
                break;
              case 'city':
              case 'province':
              case 'region':
              case 'lead_source':
              case 'client_category':
                // Filtri testuali con ricerca parziale
                if (typeof value === 'string' && value.trim()) {
                  query = query.ilike(key, `%${value}%`);
                }
                break;
              case 'entity_type':
                // Skip filter if "all" is selected (sentinel value)
                if (value !== 'all') {
                  query = query.eq(key, value);
                }
                break;
              case 'newsletter_subscribed':
                if (value === true || value === 'true') {
                  query = query.eq('newsletter_subscribed', true);
                } else if (value === false || value === 'false') {
                  query = query.or('newsletter_subscribed.is.null,newsletter_subscribed.eq.false');
                }
                break;
              default:
                // Filtri diretti di uguaglianza
                query = query.eq(key, value);
                break;
            }
          }
        });
      }

      // Applica range di date
      if (options.dateRange) {
        query = query
          .gte('created_at', options.dateRange.from)
          .lte('created_at', options.dateRange.to);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      console.log('🔍 [EXPORT CLIENTS DEBUG] Query executed');
      console.log('  ├─ Error:', error);
      console.log('  ├─ Data received:', data?.length || 0, 'records');
      console.log('  └─ First record sample:', data?.[0] ? { business_name: data[0].business_name, id: data[0].id } : 'N/A');

      if (error) throw error;

      // Post-processing per filtri complessi
      let filteredData = data || [];
      console.log('📊 [EXPORT CLIENTS DEBUG] Data received:', filteredData.length, 'records');

      // Note: Complex filters like selected_tags and selected_service will be applied
      // after fetching related data, since we simplified the main query

      // Fetch client states separately
      let clientStates = [];
      try {
        const { data: statesData, error: statesError } = await supabase
          .from('client_states')
          .select('id, name, color, is_lead')
          .eq('organization_id', userProfile.organization_id);
        
        if (statesError) {
          console.warn('Could not fetch client states:', statesError);
        } else {
          clientStates = statesData || [];
        }
      } catch (error) {
        console.warn('Client states table not available:', error);
      }

      // Filter out leads - only export actual clients (not leads)
      console.log('🚫 [EXPORT CLIENTS DEBUG] Filtering out leads');
      const beforeLeadFilter = filteredData.length;
      const clientStatesMap = new Map(clientStates?.map(state => [state.id, state]) || []);
      filteredData = filteredData.filter(client => {
        const clientState = clientStatesMap.get(client.stato_cliente);
        return clientState?.is_lead !== true; // Exclude clients where is_lead is true
      });
      console.log('  └─ After lead filter:', filteredData.length, 'records (removed', beforeLeadFilter - filteredData.length, 'leads)');

      // Fetch related data separately for all clients
      const clientIds = filteredData?.map(client => client.id) || [];
      let clientTagsMap = new Map();
      let clientDealsMap = new Map();
      let assignedUsersMap = new Map();
      
      if (clientIds.length > 0) {
        let tagAssignments = [];
        try {
          const { data: tagsData, error: tagError } = await supabase
            .from('client_tag_assignments')
            .select(`
              client_id,
              client_tags!inner (
                id,
                name,
                color,
                sort_order
              )
            `)
            .in('client_id', clientIds)
            .eq('organization_id', userProfile.organization_id)
            .eq('client_tags.is_active', true);

          if (tagError) {
            console.warn('Could not fetch client tags:', tagError);
          } else {
            tagAssignments = tagsData || [];
          }
        } catch (error) {
          console.warn('Client tags table not available:', error);
        }

        if (tagAssignments && tagAssignments.length > 0) {
          // Group tags by client_id
          tagAssignments.forEach(assignment => {
            if (!clientTagsMap.has(assignment.client_id)) {
              clientTagsMap.set(assignment.client_id, []);
            }
            clientTagsMap.get(assignment.client_id).push(assignment.client_tags);
          });

          // Sort tags within each client by sort_order
          clientTagsMap.forEach((tags, clientId) => {
            tags.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
          });
        }

        // Fetch deals for all clients
        try {
          const { data: dealsData, error: dealsError } = await supabase
            .from('deals')
            .select('id, amount, client_id, status')
            .in('client_id', clientIds)
            .eq('organization_id', userProfile.organization_id);

          if (dealsError) {
            console.warn('Could not fetch client deals:', dealsError);
          } else if (dealsData) {
            // Group deals by client_id
            dealsData.forEach(deal => {
              if (!clientDealsMap.has(deal.client_id)) {
                clientDealsMap.set(deal.client_id, []);
              }
              clientDealsMap.get(deal.client_id).push(deal);
            });
          }
        } catch (error) {
          console.warn('Deals table not available:', error);
        }

        // Fetch assigned users for all clients
        const assignedUserIds = [...new Set(filteredData.map(c => c.assigned_to).filter(Boolean))];
        if (assignedUserIds.length > 0) {
          try {
            const { data: usersData, error: usersError } = await supabase
              .from('user_profiles')
              .select('id, full_name, email')
              .in('id', assignedUserIds);

            if (usersError) {
              console.warn('Could not fetch assigned users:', usersError);
            } else if (usersData) {
              usersData.forEach(user => {
                assignedUsersMap.set(user.id, user);
              });
            }
          } catch (error) {
            console.warn('User profiles table not available:', error);
          }
        }
      }

      // Apply complex filters after fetching related data
      if (options.filters) {
        // Filtro per tag selezionati
        if (options.filters.selected_tags && Array.isArray(options.filters.selected_tags) && options.filters.selected_tags.length > 0) {
          console.log('🏷️  [EXPORT CLIENTS DEBUG] Applying tags filter:', options.filters.selected_tags);
          const beforeTagFilter = filteredData.length;
          filteredData = filteredData.filter(client => {
            const clientTags = clientTagsMap.get(client.id) || [];
            const clientTagIds = clientTags.map((tag: any) => tag.id).filter(Boolean);
            return options.filters.selected_tags.every(tagId => clientTagIds.includes(tagId));
          });
          console.log('  └─ After tags filter:', filteredData.length, 'records (removed', beforeTagFilter - filteredData.length, ')');
        }

        // Filtro per servizio selezionato
        if (options.filters.selected_service) {
          console.log('🛠️  [EXPORT CLIENTS DEBUG] Applying service filter:', options.filters.selected_service);
          const beforeServiceFilter = filteredData.length;
          filteredData = filteredData.filter(client => {
            const deals = clientDealsMap.get(client.id) || [];
            return deals.some((deal: any) =>
              deal.deal_services?.some((service: any) => service.service_id === options.filters.selected_service)
            );
          });
          console.log('  └─ After service filter:', filteredData.length, 'records (removed', beforeServiceFilter - filteredData.length, ')');
        }
      }

      console.log('📋 [EXPORT CLIENTS DEBUG] Preparing export data');
      console.log('  ├─ Filtered data:', filteredData?.length || 0, 'records');
      console.log('  ├─ Client deals map size:', clientDealsMap.size);
      console.log('  ├─ Client tags map size:', clientTagsMap.size);
      console.log('  ├─ Client states map size:', clientStatesMap.size);
      console.log('  └─ Assigned users map size:', assignedUsersMap.size);

      // Prepara i dati per l'esportazione
      const exportData = filteredData?.map((client, index) => {
        // Get deals from the map instead of client.deals
        const deals = clientDealsMap.get(client.id) || [];
        const numberOfDeals = deals.length; // All deals count

        // Calculate revenue only from accepted deals
        const acceptedDeals = deals.filter((deal: any) => deal.status === 'accepted');
        const totalRevenue = acceptedDeals.reduce((sum: number, deal: any) => sum + (deal.amount || 0), 0);

        // Get client tags from the map
        const clientTags = clientTagsMap.get(client.id) || [];
        const tagNames = clientTags.map((tag: any) => tag.name).join(', ');

        // Get assigned user from the map
        const assignedUser = client.assigned_to ? assignedUsersMap.get(client.assigned_to) : null;

        return {
          '#': index + 1,
          'Ragione Sociale': client.business_name || '',
          'Tipo Entità': client.entity_type === 'individual' ? 'Persona Fisica' : 'Azienda',
          'Partita IVA': client.vat_number || '',
          'Codice Fiscale': client.tax_code || '',
          'Email': client.email || '',
          'Telefono': client.phone || '',
          'Via': client.street_address || '',
          'Numero Civico': client.street_number || '',
          'Città': client.city || '',
          'CAP': client.postal_code || '',
          'Provincia': client.province || '',
          'Codice Paese': client.country_code || '',
          'Stato Cliente': clientStatesMap.get(client.stato_cliente)?.name || this.translateClientStatus(client.stato_cliente),
          'Assegnato a': assignedUser?.full_name || '',
          'Email Assegnato': assignedUser?.email || '',
          'Tag Clienti': tagNames,
          'Newsletter Iscritto': client.newsletter_subscribed ? 'Sì' : 'No',
          'Email Newsletter': client.newsletter_email || '',
          'Numero Trattative': numberOfDeals,
          'Fatturato Totale': this.formatCurrency(totalRevenue),
          'Data Creazione': this.formatDate(client.created_at),
          'Ultimo Aggiornamento': this.formatDate(client.updated_at)
        };
      }) || [];

      console.log('✅ [EXPORT CLIENTS DEBUG] Export data ready');
      console.log('  ├─ Total records to export:', exportData.length);
      console.log('  └─ Sample record:', exportData[0] ? {
        'Ragione Sociale': exportData[0]['Ragione Sociale'],
        'Email': exportData[0]['Email']
      } : 'N/A');

      await this.generateAndDownloadExcel({
        data: exportData,
        filename: `clienti_${this.getDateString()}.xlsx`,
        sheetName: 'Clienti'
      });

    } catch (error) {
      console.error('Error exporting clients:', error);
      throw new Error('Errore durante l\'esportazione dei clienti');
    }
  }

  /**
   * Esporta lead in Excel
   */
  async exportLeads(options: ExportOptions = {}): Promise<void> {
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      let query = supabase
        .from('clients')
        .select(`
          id,
          business_name,
          vat_number,
          tax_code,
          email,
          phone,
          whatsapp_number,
          street_address,
          street_number,
          city,
          postal_code,
          province,
          region,
          country,
          country_code,
          continent,
          sdi_code,
          stato_cliente,
          lead_source,
          lead_stage,
          lead_score,
          entity_type,
          last_activity_date,
          lead_received_date,
          pec,
          ateco_code,
          chamber_of_commerce,
          email_amministrazione,
          iban,
          abi,
          cab,
          email_legale_rappresentante,
          client_category,
          client_code,
          contact_name,
          contact_role,
          website,
          instagram_url, facebook_url, linkedin_url, youtube_url, tiktok_url,
          trade_name,
          spoken_languages,
          macro_area,
          micro_area,
          general_comments,
          accounting_regime,
          newsletter_subscribed,
          newsletter_email,
          pipeline_id,
          custom_fields,
          created_at,
          updated_at,
          deals(id, amount, status),
          assigned_user:user_profiles!clients_assigned_to_fkey(full_name, email),
          sales_rep:user_profiles!clients_sales_rep_id_fkey(full_name, email),
          initial_agent:user_profiles!clients_initial_agent_id_fkey(full_name, email),
          assigned_agent:user_profiles!clients_assigned_agent_id_fkey(full_name, email),
          referrer:referrers(id, full_name),
          partner:external_partners(id, name)
        `)
        .eq('organization_id', userProfile.organization_id);

      // Applica filtri se presenti
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          // Skip undefined, null, empty strings, and empty arrays
          if (value === undefined || value === null || value === '') {
            return;
          }
          if (Array.isArray(value) && value.length === 0) {
            return;
          }

          // Special handling for pipeline_id filter
          if (key === 'pipeline_id') {
            if (value === 'legacy') {
              // Filter for leads WITHOUT a pipeline (null or empty pipeline_id)
              query = query.is('pipeline_id', null);
            } else {
              // Filter for leads with specific pipeline
              query = query.eq(key, value);
            }
          } else {
            query = query.eq(key, value);
          }
        });
      }

      // Applica range di date
      if (options.dateRange) {
        query = query
          .gte('created_at', options.dateRange.from)
          .lte('created_at', options.dateRange.to);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch client states separately to filter for leads
      let clientStates = [];
      try {
        const { data: statesData, error: statesError } = await supabase
          .from('client_states')
          .select('id, name, color, is_lead')
          .eq('organization_id', userProfile.organization_id);

        if (statesError) {
          console.warn('Could not fetch client states:', statesError);
        } else {
          clientStates = statesData || [];
        }
      } catch (error) {
        console.warn('Client states table not available:', error);
      }

      // Filter only leads based on client_state.is_lead
      const clientStatesMap = new Map(clientStates?.map(state => [state.id, state]) || []);
      const leadsData = data?.filter(client => {
        const clientState = clientStatesMap.get(client.stato_cliente);
        return clientState?.is_lead === true;
      }) || [];

      // Fetch lead stages (custom states with is_lead = true) to map lead_stage IDs to names
      let leadStages = [];
      try {
        const { data: leadStagesData, error: leadStagesError } = await supabase
          .from('client_states')
          .select('id, name')
          .eq('organization_id', userProfile.organization_id)
          .eq('is_lead', true);

        if (!leadStagesError && leadStagesData) {
          leadStages = leadStagesData;
        }
      } catch (error) {
        console.warn('Could not fetch lead stages:', error);
      }

      const leadStagesMap = new Map(leadStages?.map(stage => [stage.id, stage.name]) || []);

      // Fetch client tags separately for all leads
      const leadIds = leadsData?.map(lead => lead.id) || [];
      let leadTagsMap = new Map();
      
      if (leadIds.length > 0) {
        const { data: tagAssignments, error: tagError } = await supabase
          .from('client_tag_assignments')
          .select(`
            client_id,
            client_tags!inner (
              id,
              name,
              color,
              sort_order
            )
          `)
          .in('client_id', leadIds)
          .eq('organization_id', userProfile.organization_id)
          .eq('client_tags.is_active', true);

        if (!tagError && tagAssignments) {
          // Group tags by client_id
          tagAssignments.forEach(assignment => {
            if (!leadTagsMap.has(assignment.client_id)) {
              leadTagsMap.set(assignment.client_id, []);
            }
            leadTagsMap.get(assignment.client_id).push(assignment.client_tags);
          });
          
          // Sort tags within each lead by sort_order
          leadTagsMap.forEach((tags, leadId) => {
            tags.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
          });
        }
      }

      // Prepara i dati per l'esportazione
      const exportData = leadsData?.map((lead, index) => {
        // Calcola numero trattative e fatturato totale manualmente
        const deals = lead.deals || [];
        const numberOfDeals = deals.length; // All deals count

        // Calculate revenue only from accepted deals
        const acceptedDeals = deals.filter((deal: any) => deal.status === 'accepted');
        const totalRevenue = acceptedDeals.reduce((sum: number, deal: any) => sum + (deal.amount || 0), 0);

        // Get lead tags
        const leadTags = leadTagsMap.get(lead.id) || [];
        const tagNames = leadTags.map((tag: any) => tag.name).join(', ');

        // Format spoken languages if present
        const spokenLanguages = lead.spoken_languages && Array.isArray(lead.spoken_languages)
          ? lead.spoken_languages.join(', ')
          : '';

        return {
          '#': index + 1,
          'Ragione Sociale': lead.business_name || '',
          'Nome Insegna': lead.trade_name || '',
          'Tipo Entità': lead.entity_type === 'individual' ? 'Persona Fisica' : 'Azienda',
          'Partita IVA': lead.vat_number || '',
          'Codice Fiscale': lead.tax_code || '',
          'Codice SDI': lead.sdi_code || '',
          'PEC': lead.pec || '',
          'Codice ATECO': lead.ateco_code || '',
          'Camera di Commercio': lead.chamber_of_commerce || '',
          'Regime Contabile': lead.accounting_regime || '',
          'Codice Cliente': lead.client_code || '',

          // Contatti
          'Email': lead.email || '',
          'Telefono': lead.phone || '',
          'WhatsApp': lead.whatsapp_number || '',
          'Sito Web': lead.website || '',
          'Instagram': lead.instagram_url || '',
          'Facebook': lead.facebook_url || '',
          'Linkedin': lead.linkedin_url || '',
          'Youtube': lead.youtube_url || '',
          'Tiktok': lead.tiktok_url || '',

          'Nome Contatto': lead.contact_name || '',
          'Ruolo Contatto': lead.contact_role || '',
          'Email Amministrazione': lead.email_amministrazione || '',
          'Email Legale Rappresentante': lead.email_legale_rappresentante || '',

          // Indirizzo
          'Via': lead.street_address || '',
          'Numero Civico': lead.street_number || '',
          'Città': lead.city || '',
          'CAP': lead.postal_code || '',
          'Provincia': lead.province || '',
          'Regione': regionDisplayName(lead.region),
          'Paese': lead.country || '',
          'Codice Paese': lead.country_code || '',
          'Continente': lead.continent || '',
          'Macro Area': lead.macro_area || '',
          'Micro Area': lead.micro_area || '',

          // Dati bancari
          'IBAN': lead.iban || '',
          'ABI': lead.abi || '',
          'CAB': lead.cab || '',

          // Stato e categoria
          'Stato Lead': clientStatesMap.get(lead.stato_cliente)?.name || '',
          'Stage Lead': leadStagesMap.get(lead.lead_stage) || lead.lead_stage || '',
          'Score Lead': lead.lead_score || '',
          'Categoria Cliente': lead.client_category || '',

          // Fonte e assegnazione
          'Fonte Lead': lead.lead_source_data?.name || '',
          'Data Ricezione Lead': this.formatDate(lead.lead_received_date),
          'Assegnato a': lead.assigned_user?.full_name || '',
          'Email Assegnato': lead.assigned_user?.email || '',
          'Sales Rep': lead.sales_rep?.full_name || '',
          'Email Sales Rep': lead.sales_rep?.email || '',
          'Agente Iniziale': lead.initial_agent?.full_name || '',
          'Email Agente Iniziale': lead.initial_agent?.email || '',
          'Agente Assegnato': lead.assigned_agent?.full_name || '',
          'Email Agente Assegnato': lead.assigned_agent?.email || '',
          'Referrer': lead.referrer?.full_name || '',
          'Partner': lead.partner?.name || '',

          // Altri dati
          'Lingue Parlate': spokenLanguages,
          'Commenti Generali': lead.general_comments || '',
          'Tag Lead': tagNames,
          'Newsletter Iscritto': lead.newsletter_subscribed ? 'Sì' : 'No',
          'Email Newsletter': lead.newsletter_email || '',

          // Trattative
          'Numero Trattative': numberOfDeals,
          'Fatturato Totale': this.formatCurrency(totalRevenue),

          // Date
          'Ultima Attività': this.formatDate(lead.last_activity_date),
          'Data Creazione': this.formatDate(lead.created_at),
          'Ultimo Aggiornamento': this.formatDate(lead.updated_at)
        };
      }) || [];

      await this.generateAndDownloadExcel({
        data: exportData,
        filename: `lead_${this.getDateString()}.xlsx`,
        sheetName: 'Lead'
      });

    } catch (error) {
      console.error('Error exporting leads:', error);
      throw new Error('Errore durante l\'esportazione dei lead');
    }
  }

  /**
   * Esporta trattative in Excel
   */
  async exportDeals(options: ExportOptions = {}): Promise<void> {
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      let query = supabase
        .from('deals')
        .select(`
          id,
          title,
          description,
          amount,
          status,
          created_at,
          closed_at,
          signed_at,
          quote_number,
          client:clients(business_name, vat_number),
          assignee:user_profiles!deals_assigned_to_fkey(full_name, email),
          deal_services(
            amount,
            service:services(title, service_type)
          )
        `)
        .eq('organization_id', userProfile.organization_id);

      // Applica filtri se presenti
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (key === 'assigned_to') {
              query = query.eq('assigned_to', value);
            } else if (key === 'client_id') {
              query = query.eq('client_id', value);
            } else if (key === 'status') {
              query = query.eq('status', value);
            }
          }
        });
      }

      // Applica range di date
      if (options.dateRange) {
        query = query
          .gte('created_at', options.dateRange.from)
          .lte('created_at', options.dateRange.to);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      // Prepara i dati per l'esportazione
      const exportData = data?.map((deal, index) => ({
        '#': index + 1,
        'Numero Offerta': deal.quote_number || '',
        'Titolo': deal.title || '',
        'Descrizione': deal.description || '',
        'Cliente': deal.client?.business_name || '',
        'P.IVA Cliente': deal.client?.vat_number || '',
        'Importo': this.formatCurrency(deal.amount || 0),
        'Stato': this.translateDealStatus(deal.status),
        'Assegnato a': deal.assignee?.full_name || '',
        'Email Assegnatario': deal.assignee?.email || '',
        'Numero Servizi': deal.deal_services?.length || 0,
        'Servizi': deal.deal_services?.map(ds => ds.service?.title).join(', ') || '',
        'Data Creazione': this.formatDate(deal.created_at),
        'Data Chiusura': this.formatDate(deal.closed_at),
        'Data Firma': this.formatDate(deal.signed_at)
      })) || [];

      await this.generateAndDownloadExcel({
        data: exportData,
        filename: `trattative_${this.getDateString()}.xlsx`,
        sheetName: 'Trattative'
      });

    } catch (error) {
      console.error('Error exporting deals:', error);
      throw new Error('Errore durante l\'esportazione delle trattative');
    }
  }

  /**
   * Esporta servizi in Excel
   */
  async exportServices(options: ExportOptions = {}): Promise<void> {
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      let query = supabase
        .from('services')
        .select(`
          id,
          title,
          description,
          short_description,
          list_price,
          service_type,
          auto_invoice,
          created_at,
          updated_at,
          service_category:service_categories(name),
          deal_services(id, amount)
        `)
        .eq('organization_id', userProfile.organization_id);

      // Applica filtri se presenti
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            query = query.eq(key, value);
          }
        });
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      // Prepara i dati per l'esportazione
      const exportData = data?.map((service, index) => {
        // Calcola numero di vendite e fatturato totale manualmente
        const dealServices = service.deal_services || [];
        const timesSold = dealServices.length;
        const totalRevenue = dealServices.reduce((sum: number, ds: any) => sum + (ds.amount || 0), 0);

        return {
          '#': index + 1,
          'Titolo': service.title || '',
          'Descrizione Breve': service.short_description || '',
          'Descrizione Completa': service.description || '',
          'Prezzo Listino': this.formatCurrency(service.list_price || 0),
          'Tipo Servizio': this.translateServiceType(service.service_type),
          'Fatturazione Automatica': service.auto_invoice ? 'Sì' : 'No',
          'Categoria': service.service_category?.name || '',
          'Volte Venduto': timesSold,
          'Fatturato Totale': this.formatCurrency(totalRevenue),
          'Data Creazione': this.formatDate(service.created_at),
          'Ultimo Aggiornamento': this.formatDate(service.updated_at)
        };
      }) || [];

      await this.generateAndDownloadExcel({
        data: exportData,
        filename: `servizi_${this.getDateString()}.xlsx`,
        sheetName: 'Servizi'
      });

    } catch (error) {
      console.error('Error exporting services:', error);
      throw new Error('Errore durante l\'esportazione dei servizi');
    }
  }

  /**
   * Esporta fatture in Excel
   */
  async exportInvoices(options: ExportOptions = {}): Promise<void> {
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      let query = supabase
        .from('invoices')
        .select(`
          id,
          number,
          date,
          due_date,
          amount,
          tax_amount,
          total_amount,
          status,
          payment_status,
          created_at,
          deal_id,
          deal:deals(
            title,
            quote_number,
            organization_id,
            client:clients(
              business_name,
              vat_number
            )
          )
        `)
        .eq('deal.organization_id', userProfile.organization_id);

      // Applica filtri se presenti
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (key === 'deal_id') {
              query = query.eq('deal_id', value);
            } else {
              query = query.eq(key, value);
            }
          }
        });
      }

      // Applica range di date
      if (options.dateRange) {
        query = query
          .gte('date', options.dateRange.from)
          .lte('date', options.dateRange.to);
      }

      const { data, error } = await query.order('date', { ascending: false });

      if (error) throw error;

      // Prepara i dati per l'esportazione
      const exportData = data?.map((invoice, index) => ({
        '#': index + 1,
        'Numero Fattura': invoice.number || '',
        'Data Fattura': this.formatDate(invoice.date),
        'Data Scadenza': this.formatDate(invoice.due_date),
        'Cliente': invoice.deal?.client?.business_name || '',
        'P.IVA Cliente': invoice.deal?.client?.vat_number || '',
        'Trattativa': invoice.deal?.title || '',
        'N. Offerta': invoice.deal?.quote_number || '',
        'Imponibile': this.formatCurrency(invoice.amount || 0),
        'Imposta': this.formatCurrency(invoice.tax_amount || 0),
        'Totale': this.formatCurrency(invoice.total_amount || 0),
        'Stato Fattura': this.translateInvoiceStatus(invoice.status),
        'Stato Pagamento': this.translatePaymentStatus(invoice.payment_status),
        'Data Creazione': this.formatDate(invoice.created_at)
      })) || [];

      await this.generateAndDownloadExcel({
        data: exportData,
        filename: `fatture_${this.getDateString()}.xlsx`,
        sheetName: 'Fatture'
      });

    } catch (error) {
      console.error('Error exporting invoices:', error);
      throw new Error('Errore durante l\'esportazione delle fatture');
    }
  }

  /**
   * Esporta commissioni in Excel
   */
  async exportCommissions(commissions: any[], startDate: Date, endDate?: Date): Promise<void> {
    try {
      // Ordina le commissioni per nome persona (alfabetico)
      const sortedCommissions = [...commissions].sort((a, b) => {
        return (a.person_name || '').localeCompare(b.person_name || '', 'it-IT');
      });

      // Prepara i dati per l'esportazione
      const exportData = sortedCommissions.map((entry, index) => {
        // Format service type
        const serviceTypeLabel = entry.service_type === 'monthly' ? 'Mensile' :
                                entry.service_type === 'annual' ? 'Annuale' :
                                entry.service_type === 'one_time' ? 'Una tantum' : '';

        return {
          '#': index + 1,
          'Persona': entry.person_name || '',
          'Tipo': entry.is_agent ? 'Agente' : 'Segnalatore',
          'Offerta': entry.title || '',
          'Numero Offerta': entry.quote_number || '',
          'Cliente': entry.client_name || '',
          'Servizio': entry.service_title || 'Deal generale',
          'Tipo Servizio': serviceTypeLabel,
          'Categoria Servizio': entry.service_category || '',
          'Data Chiusura': this.formatDate(entry.closed_at),
          'Importo Base': entry.amount || 0,
          '% Commissione': `${entry.commission_rate || 0}%`,
          'Commissione': entry.commission_amount || 0,
          // Tracking incasso
          'Totale Fatturato': entry.total_invoiced || 0,
          'Incasso': entry.total_paid || 0,
          '% Incasso': entry.payment_percentage != null ? `${Math.round(entry.payment_percentage)}%` : '',
          'Data Incasso': entry.paid_date ? this.formatDateShort(entry.paid_date) : '',
          // Retrocessioni
          'Retrocessione Pagabile': Math.max(0, (entry.commission_cash || 0) - (entry.total_paid_to_consultant || 0)),
          'Retrocessione Sospesa': entry.commission_outstanding || 0,
          // Pagamenti a consulente
          'Pagato a Consulente': entry.total_paid_to_consultant || 0,
          'Data Pag. Consulente': entry.last_paid_to_consultant_date ? this.formatDate(entry.last_paid_to_consultant_date) : '',
          // Stato pagamento commissione
          'Stato Pagamento': entry.paid ? 'Pagato' : 'Non pagato'
        };
      });

      // Genera il nome del file in base al tipo di esportazione
      let filename: string;
      if (endDate) {
        // Range mode: usa formato data dd-MM-yyyy
        const startStr = format(startDate, 'dd-MM-yyyy');
        const endStr = format(endDate, 'dd-MM-yyyy');
        filename = `commissioni_${startStr}_${endStr}_${this.getDateString()}.xlsx`;
      } else {
        // Single month mode: usa formato mese_anno
        const monthName = format(startDate, 'MMMM_yyyy', { locale: it });
        filename = `commissioni_${monthName}_${this.getDateString()}.xlsx`;
      }

      await this.generateAndDownloadExcel({
        data: exportData,
        filename: filename,
        sheetName: 'Commissioni'
      });

    } catch (error) {
      console.error('Error exporting commissions:', error);
      throw new Error('Errore durante l\'esportazione delle commissioni');
    }
  }

  /**
   * Esporta fatture da Fatture in Cloud in Excel con filtri avanzati
   */
  async exportFICInvoices(options: {
    organizationId: string;
    year: number;
    selectedMonths: number[];
    hasStampDuty?: boolean;
    paymentStatus?: 'paid' | 'partial' | 'unpaid' | 'all';
    minAmount?: number;
    maxAmount?: number;
    isExempt?: boolean;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<void> {
    try {
      console.log('🚀 [EXPORT FIC INVOICES] Starting export with options:', options);

      // Query fattureincloud_invoices
      let query = supabase
        .from('fattureincloud_invoices')
        .select('*')
        .eq('organization_id', options.organizationId)
        .order('date', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      let filteredData = data || [];
      console.log('📊 [EXPORT FIC INVOICES] Total invoices fetched:', filteredData.length);

      // Filtro per anno
      filteredData = filteredData.filter(inv => {
        if (!inv.date) return false;
        const invYear = new Date(inv.date).getFullYear();
        return invYear === options.year;
      });
      console.log('📊 [EXPORT FIC INVOICES] After year filter:', filteredData.length);

      // Filtro per mesi selezionati
      if (options.selectedMonths && options.selectedMonths.length > 0) {
        filteredData = filteredData.filter(inv => {
          if (!inv.date) return false;
          const invMonth = new Date(inv.date).getMonth(); // 0-11
          return options.selectedMonths.includes(invMonth);
        });
        console.log('📊 [EXPORT FIC INVOICES] After month filter:', filteredData.length);
      }

      // Filtro marca da bollo - stessa logica del badge InvoiceTable
      if (options.hasStampDuty) {
        filteredData = filteredData.filter(inv => {
          const vatAmount = inv.data?.amount_vat || 0;
          const stampDuty = inv.data?.stamp_duty || inv.stamp_duty || 0;
          const hasStampDutyItem = inv.data?.items_list?.some(
            (i: any) => i.vat?.id === 21 || (i.not_taxable && i.name?.toLowerCase().includes('bollo'))
          );
          // Bollo = vatAmount === 0 AND (stampDuty > 0 OR hasStampDutyItem)
          return vatAmount === 0 && (stampDuty > 0 || hasStampDutyItem);
        });
        console.log('📊 [EXPORT FIC INVOICES] After stamp duty filter:', filteredData.length);
      }

      // Filtro stato pagamento
      if (options.paymentStatus && options.paymentStatus !== 'all') {
        filteredData = filteredData.filter(inv => {
          const status = inv.status?.toLowerCase();
          switch (options.paymentStatus) {
            case 'paid':
              return status === 'paid';
            case 'partial':
              return status === 'partial';
            case 'unpaid':
              return status === 'issued' || status === 'not_paid' || status === 'overdue';
            default:
              return true;
          }
        });
        console.log('📊 [EXPORT FIC INVOICES] After payment status filter:', filteredData.length);
      }

      // Filtro importo minimo
      if (options.minAmount !== undefined && options.minAmount > 0) {
        filteredData = filteredData.filter(inv => {
          const amount = inv.data?.amount_gross || inv.amount || 0;
          return amount >= options.minAmount!;
        });
        console.log('📊 [EXPORT FIC INVOICES] After min amount filter:', filteredData.length);
      }

      // Filtro importo massimo
      if (options.maxAmount !== undefined && options.maxAmount > 0) {
        filteredData = filteredData.filter(inv => {
          const amount = inv.data?.amount_gross || inv.amount || 0;
          return amount <= options.maxAmount!;
        });
        console.log('📊 [EXPORT FIC INVOICES] After max amount filter:', filteredData.length);
      }

      // Filtro esente IVA
      if (options.isExempt) {
        filteredData = filteredData.filter(inv => {
          // Controlla se la fattura ha items con IVA 0% o esente
          const items = inv.data?.items_list || [];
          const hasExemptItems = items.some((item: any) =>
            item.vat?.value === 0 ||
            item.not_taxable === true ||
            item.vat?.is_disabled === true
          );
          // Oppure controlla amount_vat = 0
          const amountVat = inv.data?.amount_vat || 0;
          return hasExemptItems || amountVat === 0;
        });
        console.log('📊 [EXPORT FIC INVOICES] After exempt filter:', filteredData.length);
      }

      // Filtro range date
      if (options.dateFrom) {
        const filterDateFrom = new Date(options.dateFrom);
        filteredData = filteredData.filter(inv => {
          if (!inv.date) return false;
          const invDate = new Date(inv.date);
          return invDate >= filterDateFrom;
        });
        console.log('📊 [EXPORT FIC INVOICES] After dateFrom filter:', filteredData.length);
      }

      if (options.dateTo) {
        const filterDateTo = new Date(options.dateTo);
        filterDateTo.setHours(23, 59, 59, 999); // Include entire day
        filteredData = filteredData.filter(inv => {
          if (!inv.date) return false;
          const invDate = new Date(inv.date);
          return invDate <= filterDateTo;
        });
        console.log('📊 [EXPORT FIC INVOICES] After dateTo filter:', filteredData.length);
      }

      // Mappa dati per Excel
      const exportData = filteredData.map((inv, index) => {
        const entityData = inv.data?.entity || {};
        const amountNet = inv.data?.amount_net || inv.amount || 0;
        const amountVat = inv.data?.amount_vat || 0;
        const amountGross = inv.data?.amount_gross || inv.amount || 0;
        const paidAmount = inv.paid_amount || inv.data?.payments_sum || 0;

        // Cerca stamp_duty nel campo dedicato o negli items
        const stampDutyFromField = inv.data?.stamp_duty || inv.stamp_duty || 0;
        const stampDutyItem = inv.data?.items_list?.find(
          (i: any) => i.vat?.id === 21 || (i.not_taxable && i.name?.toLowerCase().includes('bollo'))
        );
        const stampDutyValue = stampDutyFromField > 0
          ? stampDutyFromField
          : (stampDutyItem?.gross_price || stampDutyItem?.net_price || 0);

        return {
          '#': index + 1,
          'Numero': inv.number || '',
          'Tipo': this.translateFICDocumentType(inv.type),
          'Data': this.formatDateShort(inv.date),
          'Scadenza': this.formatDateShort(inv.due_date),
          'Cliente': entityData.name || inv.client_name || '',
          'P.IVA': entityData.vat_number || '',
          'Cod. Fiscale': entityData.tax_code || '',
          'Imponibile': amountNet,
          'IVA': amountVat,
          'Totale': amountGross,
          'Marca da Bollo': stampDutyValue > 0 ? stampDutyValue : '',
          'Stato': this.translateFICPaymentStatus(inv.status),
          'Pagato': paidAmount,
          'Residuo': amountGross - paidAmount > 0 ? amountGross - paidAmount : 0
        };
      });

      if (exportData.length === 0) {
        throw new Error('Nessuna fattura trovata con i filtri selezionati');
      }

      await this.generateAndDownloadExcel({
        data: exportData,
        filename: `fatture_fic_${options.year}_${this.getDateString()}.xlsx`,
        sheetName: 'Fatture'
      });

      console.log('✅ [EXPORT FIC INVOICES] Export completed:', exportData.length, 'invoices');

    } catch (error) {
      console.error('Error exporting FIC invoices:', error);
      throw error instanceof Error ? error : new Error('Errore durante l\'esportazione delle fatture');
    }
  }

  /**
   * Esporta la coda fatture da emettere (invoice_queue + enhanced_invoice_queue)
   */
  async exportInvoiceQueue(options: {
    organizationId: string;
    status?: 'pending' | 'processed' | 'error' | 'cancelled' | 'all';
    dateFrom?: string;
    dateTo?: string;
    documentType?: 'installment' | 'variable' | 'invoice' | 'all';
  }): Promise<void> {
    try {
      console.log('📊 [EXPORT INVOICE QUEUE] Starting export with options:', options);

      // Query invoice_queue
      let invoiceQueueQuery = supabase
        .from('invoice_queue')
        .select(`
          *,
          deal:deals(
            id,
            title,
            client:clients(
              id,
              business_name
            )
          ),
          deal_service:deal_services(
            id,
            service:services(
              id,
              title
            )
          )
        `)
        .eq('organization_id', options.organizationId);

      // Query enhanced_invoice_queue
      let enhancedQueueQuery = supabase
        .from('enhanced_invoice_queue')
        .select(`
          *,
          deal:deals(
            id,
            title,
            client:clients(
              id,
              business_name
            )
          )
        `)
        .eq('organization_id', options.organizationId);

      // Apply status filter
      if (options.status && options.status !== 'all') {
        invoiceQueueQuery = invoiceQueueQuery.eq('status', options.status);
        enhancedQueueQuery = enhancedQueueQuery.eq('status', options.status);
      }

      // Apply date filters
      if (options.dateFrom) {
        invoiceQueueQuery = invoiceQueueQuery.gte('due_date', options.dateFrom);
        enhancedQueueQuery = enhancedQueueQuery.gte('due_date', options.dateFrom);
      }
      if (options.dateTo) {
        invoiceQueueQuery = invoiceQueueQuery.lte('due_date', options.dateTo);
        enhancedQueueQuery = enhancedQueueQuery.lte('due_date', options.dateTo);
      }

      // Execute queries
      const [invoiceQueueResult, enhancedQueueResult] = await Promise.all([
        invoiceQueueQuery.order('due_date', { ascending: true }),
        enhancedQueueQuery.order('due_date', { ascending: true })
      ]);

      if (invoiceQueueResult.error) throw invoiceQueueResult.error;
      if (enhancedQueueResult.error) throw enhancedQueueResult.error;

      // Map invoice_queue items
      const invoiceQueueItems = (invoiceQueueResult.data || []).map((item: any) => ({
        ...item,
        source: 'invoice_queue',
        client_name: item.deal?.client?.business_name || 'Cliente',
        service_name: item.deal_service?.service?.title || item.installment_description || 'Servizio',
        deal_title: item.deal?.title || '',
        document_type: item.metadata?.billing_type === 'variable' ? 'variable' : 'installment'
      }));

      // Map enhanced_invoice_queue items
      const enhancedQueueItems = (enhancedQueueResult.data || []).map((item: any) => ({
        ...item,
        source: 'enhanced_invoice_queue',
        client_name: item.fic_data?.entity?.name || item.deal?.client?.business_name || 'Cliente',
        service_name: item.fic_data?.items?.[0]?.name || 'Fattura avanzata',
        deal_title: item.deal?.title || '',
        document_type: 'invoice'
      }));

      // Merge all items
      let allItems = [...invoiceQueueItems, ...enhancedQueueItems];

      // Filter by document type
      if (options.documentType && options.documentType !== 'all') {
        allItems = allItems.filter(item => item.document_type === options.documentType);
      }

      // Sort by due_date
      allItems.sort((a, b) => {
        const dateA = a.due_date ? new Date(a.due_date) : new Date(0);
        const dateB = b.due_date ? new Date(b.due_date) : new Date(0);
        return dateA.getTime() - dateB.getTime();
      });

      console.log('📊 [EXPORT INVOICE QUEUE] Total items:', allItems.length);

      if (allItems.length === 0) {
        throw new Error('Nessun elemento nella coda con i filtri selezionati');
      }

      // Map to Excel format
      const exportData = allItems.map((item, index) => ({
        '#': index + 1,
        'Cliente': item.client_name,
        'Descrizione': item.service_name || item.installment_description || '',
        'Tipo': this.translateQueueDocumentType(item.document_type),
        'Importo': item.amount || 0,
        'Data Scadenza': this.formatDateShort(item.due_date),
        'Stato': this.translateQueueStatus(item.status),
        'Deal': item.deal_title,
        'Note': item.notes || ''
      }));

      await this.generateAndDownloadExcel({
        data: exportData,
        filename: `coda_fatture_${this.getDateString()}.xlsx`,
        sheetName: 'Coda Fatture'
      });

      console.log('✅ [EXPORT INVOICE QUEUE] Export completed:', exportData.length, 'items');

    } catch (error) {
      console.error('Error exporting invoice queue:', error);
      throw error instanceof Error ? error : new Error('Errore durante l\'esportazione della coda');
    }
  }

  private translateQueueDocumentType(type?: string): string {
    const translations: Record<string, string> = {
      'installment': 'Rata',
      'variable': 'Variabile',
      'invoice': 'Fattura'
    };
    return translations[type || ''] || type || '';
  }

  private translateQueueStatus(status?: string): string {
    const translations: Record<string, string> = {
      'pending': 'In Attesa',
      'processing': 'In Elaborazione',
      'processed': 'Elaborato',
      'error': 'Errore',
      'cancelled': 'Annullato'
    };
    return translations[status || ''] || status || '';
  }

  private translateFICDocumentType(type?: string): string {
    const translations: Record<string, string> = {
      'invoice': 'Fattura',
      'credit_note': 'Nota di Credito',
      'receipt': 'Ricevuta',
      'proforma': 'Proforma',
      'delivery_note': 'DDT'
    };
    return translations[type || ''] || type || '';
  }

  private translateFICPaymentStatus(status?: string): string {
    const translations: Record<string, string> = {
      'paid': 'Pagata',
      'partial': 'Parziale',
      'issued': 'Emessa',
      'not_paid': 'Non Pagata',
      'overdue': 'Scaduta'
    };
    return translations[status || ''] || status || '';
  }

  private formatDateShort(dateString?: string): string {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: it });
    } catch {
      return dateString;
    }
  }

  /**
   * Genera e scarica il file Excel
   */
  private async generateAndDownloadExcel({ data, filename, sheetName }: ExportData): Promise<void> {
    try {
      console.log('📥 [EXPORT EXCEL DEBUG] Generating Excel file');
      console.log('  ├─ Data rows:', data?.length || 0);
      console.log('  ├─ Filename:', filename);
      console.log('  └─ Sheet name:', sheetName);

      // Crea un nuovo workbook
      const workbook = XLSX.utils.book_new();

      // Crea il worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);
      console.log('  ├─ Worksheet created, keys:', Object.keys(worksheet).filter(k => !k.startsWith('!')).slice(0, 5));

      // Apply currency format to currency columns
      this.applyCurrencyFormat(worksheet, data);

      // Imposta la larghezza delle colonne automaticamente
      const columnWidths = this.calculateColumnWidths(data);
      worksheet['!cols'] = columnWidths;

      // Aggiunge il worksheet al workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Aggiunge metadati
      workbook.Props = {
        Title: sheetName,
        Subject: `Esportazione ${sheetName}`,
        Author: 'CRM System',
        CreatedDate: new Date()
      };

      // Genera il file e avvia il download
      XLSX.writeFile(workbook, filename);

    } catch (error) {
      console.error('Error generating Excel file:', error);
      throw new Error('Errore durante la generazione del file Excel');
    }
  }

  /**
   * Calcola automaticamente la larghezza delle colonne
   */
  private calculateColumnWidths(data: any[]): any[] {
    if (!data || data.length === 0) return [];

    const columnWidths: any[] = [];
    const headers = Object.keys(data[0]);

    headers.forEach((header, index) => {
      let maxWidth = header.length;

      data.forEach(row => {
        const cellValue = String(row[header] || '');
        maxWidth = Math.max(maxWidth, cellValue.length);
      });

      // Limita la larghezza massima e minima
      maxWidth = Math.min(Math.max(maxWidth, 10), 50);

      columnWidths[index] = { width: maxWidth };
    });

    return columnWidths;
  }

  /**
   * Apply Euro currency format to currency columns in worksheet
   */
  private applyCurrencyFormat(worksheet: XLSX.WorkSheet, data: any[]): void {
    if (!data || data.length === 0) return;

    // Define currency column names (Italian labels)
    const currencyColumns = [
      'Importo Base',
      'Commissione',
      'Fatturato Totale',
      'Imponibile',
      'Imposta',
      'Totale',
      'Importo',
      'Prezzo Listino',
      'IVA',
      'Marca da Bollo',
      'Pagato',
      'Residuo',
      // Nuove colonne export commissioni
      'Totale Fatturato',
      'Incasso',
      'Retrocessione Pagabile',
      'Retrocessione Sospesa',
      'Pagato a Consulente'
    ];

    // Get headers from first data row
    const headers = Object.keys(data[0]);

    // Find indices of currency columns
    const currencyIndices: number[] = [];
    headers.forEach((header, index) => {
      if (currencyColumns.includes(header)) {
        currencyIndices.push(index);
      }
    });

    // Excel currency format for Euro (Italian locale)
    // Format: [$€-410] #,##0.00 (410 = Italian locale code)
    const euroCurrencyFormat = '[$€-410] #,##0.00';

    // Apply format to each currency cell
    data.forEach((row, rowIndex) => {
      currencyIndices.forEach(colIndex => {
        // Excel cell address (e.g., B2, C2, etc.)
        // Row index + 2 because: +1 for header row, +1 for 1-based indexing
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
        const cell = worksheet[cellAddress];

        if (cell && typeof cell.v === 'number') {
          // Set number format
          cell.z = euroCurrencyFormat;
          // Ensure cell type is number
          cell.t = 'n';
        }
      });
    });
  }

  // Utility methods per formattazione
  private formatDate(dateString?: string): string {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: it });
    } catch {
      return dateString;
    }
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  private getDateString(): string {
    return format(new Date(), 'yyyy-MM-dd_HH-mm');
  }

  // Metodi di traduzione
  private translateClientStatus(status?: string): string {
    const translations: Record<string, string> = {
      'lead': 'Lead',
      'client': 'Cliente',
      'inactive': 'Inattivo'
    };
    return translations[status || ''] || status || '';
  }

  private translateDealStatus(status?: string): string {
    const translations: Record<string, string> = {
      'draft': 'Bozza',
      'sent': 'Inviato',
      'viewed': 'Visualizzato',
      'accepted': 'Accettato',
      'rejected': 'Rifiutato',
      'expired': 'Scaduto'
    };
    return translations[status || ''] || status || '';
  }

  private translateServiceType(type?: string): string {
    const translations: Record<string, string> = {
      'one_time': 'Una tantum',
      'monthly': 'Mensile',
      'annual': 'Annuale'
    };
    return translations[type || ''] || type || '';
  }

  private translateInvoiceStatus(status?: string): string {
    const translations: Record<string, string> = {
      'draft': 'Bozza',
      'sent': 'Inviata',
      'paid': 'Pagata',
      'overdue': 'Scaduta',
      'cancelled': 'Annullata'
    };
    return translations[status || ''] || status || '';
  }

  private translatePaymentStatus(status?: string): string {
    const translations: Record<string, string> = {
      'pending': 'In attesa',
      'partial': 'Parziale',
      'paid': 'Pagato',
      'overdue': 'Scaduto'
    };
    return translations[status || ''] || status || '';
  }

  private translateProcessingStatus(status?: string): string {
    const translations: Record<string, string> = {
      'idle': 'Inattivo',
      'processing': 'In elaborazione',
      'completed': 'Completato',
      'failed': 'Fallito'
    };
    return translations[status || ''] || status || '';
  }

  /**
   * Export a report execution result in the specified format
   */
  async exportExecution(executionId: string, format: 'pdf' | 'excel' | 'csv'): Promise<{ download_url: string }> {
    try {
      // Get the execution result
      const { data: execution, error } = await supabase
        .from('report_executions')
        .select('result_data, report:custom_reports(name)')
        .eq('id', executionId)
        .single();

      if (error) throw error;
      if (!execution?.result_data) throw new Error('No execution data found');

      const reportName = execution.report?.name || 'Report';
      const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
      const filename = `${reportName}_${timestamp}`;

      let downloadUrl: string;

      switch (format) {
        case 'excel':
          downloadUrl = await this.generateExcelFromData(execution.result_data, filename);
          break;
        case 'csv':
          downloadUrl = await this.generateCSVFromData(execution.result_data, filename);
          break;
        case 'pdf':
          downloadUrl = await this.generatePDFFromData(execution.result_data, filename, reportName);
          break;
        default:
          throw new Error(`Unsupported format: ${format}`);
      }

      return { download_url: downloadUrl };
    } catch (error) {
      console.error('Error exporting execution:', error);
      throw error;
    }
  }

  private async generateExcelFromData(data: any[], filename: string): Promise<string> {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Data');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    return URL.createObjectURL(blob);
  }

  private async generateCSVFromData(data: any[], filename: string): Promise<string> {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvString = XLSX.utils.sheet_to_csv(worksheet);
    
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    return URL.createObjectURL(blob);
  }

  private async generatePDFFromData(data: any[], filename: string, reportName: string): Promise<string> {
    // For PDF generation, we'll create a simple HTML table and convert it
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const headers = Object.keys(data[0]);
    
    const htmlContent = `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; margin-bottom: 20px; }
            table { border-collapse: collapse; width: 100%; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
          </style>
        </head>
        <body>
          <h1>${reportName}</h1>
          <p>Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${data.map(row => 
                `<tr>${headers.map(h => `<td>${row[h] || ''}</td>`).join('')}</tr>`
              ).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    return URL.createObjectURL(blob);
  }

  /**
   * Count exportable records with filters
   */
  async countExportableRecords(options: {
    type: 'clients' | 'leads' | 'both';
    organizationId: string;
    filters: {
      interestIds?: string[];
      tagIds?: string[];
      countries?: string[];
      userIds?: string[];
      userFilterField?: 'assigned_to' | 'sales_rep_id';
      entityTypes?: string[];
      clientStateIds?: string[];
      leadStageIds?: string[];
      leadSourceIds?: string[];
      categories?: string[];
      referrerIds?: string[];
      dateFrom?: string;
      dateTo?: string;
      cities?: string[];
      provinces?: string[];
      regions?: string[];
      newsletterSubscribed?: boolean;
    };
    filterLogic: 'or' | 'and' | 'not';
  }): Promise<number> {
    try {
      // Get client states to determine which are leads
      const { data: clientStates } = await supabase
        .from('client_states')
        .select('id, is_lead')
        .eq('organization_id', options.organizationId);

      const clientStatesMap = new Map(clientStates?.map(s => [s.id, s.is_lead]) || []);
      const leadStateIds = clientStates?.filter(s => s.is_lead).map(s => s.id) || [];
      const clientStateIds = clientStates?.filter(s => !s.is_lead).map(s => s.id) || [];

      // Base query
      let query = supabase
        .from('clients')
        .select('id, stato_cliente, country', { count: 'exact' })
        .eq('organization_id', options.organizationId)
        .or(EXCLUDE_REFERENTI); // Fase 2: no persone-referente nel conteggio export

      // Filter by type
      if (options.type === 'clients') {
        query = query.in('stato_cliente', clientStateIds);
      } else if (options.type === 'leads') {
        query = query.in('stato_cliente', leadStateIds);
      }
      // 'both' = no filter

      // Helper to apply .in() or .not(.., 'in', ..) based on filterLogic
      const isNot = options.filterLogic === 'not';
      const applyInFilter = (q: any, column: string, values: string[]) => {
        if (isNot) return q.not(column, 'in', `(${values.join(',')})`);
        return q.in(column, values);
      };

      // Filter by country
      if (options.filters.countries && options.filters.countries.length > 0) {
        query = applyInFilter(query, 'country', options.filters.countries);
      }

      // Filter by assigned user (agente or venditore)
      if (options.filters.userIds && options.filters.userIds.length > 0) {
        const userColumn = options.filters.userFilterField || 'assigned_to';
        query = applyInFilter(query, userColumn, options.filters.userIds);
      }

      // Filter by entity type
      if (options.filters.entityTypes && options.filters.entityTypes.length > 0) {
        query = applyInFilter(query, 'entity_type', options.filters.entityTypes);
      }

      // Filter by specific client states (overrides the type-based filter)
      if (options.filters.clientStateIds && options.filters.clientStateIds.length > 0) {
        query = applyInFilter(query, 'stato_cliente', options.filters.clientStateIds);
      }

      // Filter by lead stage (check both lead_stage and pipeline_stage_id)
      if (options.filters.leadStageIds && options.filters.leadStageIds.length > 0) {
        if (isNot) {
          query = query.not('lead_stage', 'in', `(${options.filters.leadStageIds.join(',')})`);
          query = query.not('pipeline_stage_id', 'in', `(${options.filters.leadStageIds.join(',')})`);
        } else {
          query = query.or(
            `lead_stage.in.(${options.filters.leadStageIds.join(',')}),pipeline_stage_id.in.(${options.filters.leadStageIds.join(',')})`
          );
        }
      }

      // Filter by lead source
      if (options.filters.leadSourceIds && options.filters.leadSourceIds.length > 0) {
        query = applyInFilter(query, 'lead_source', options.filters.leadSourceIds);
      }

      // Filter by client category
      if (options.filters.categories && options.filters.categories.length > 0) {
        query = applyInFilter(query, 'client_category', options.filters.categories);
      }

      // Filter by referrer (segnalatore)
      if (options.filters.referrerIds && options.filters.referrerIds.length > 0) {
        query = applyInFilter(query, 'referrer_id', options.filters.referrerIds);
      }

      // Filter by date range
      if (options.filters.dateFrom) {
        if (isNot) {
          query = query.lt('created_at', options.filters.dateFrom);
        } else {
          query = query.gte('created_at', options.filters.dateFrom);
        }
      }
      if (options.filters.dateTo) {
        if (isNot) {
          query = query.gt('created_at', options.filters.dateTo + 'T23:59:59.999Z');
        } else {
          query = query.lte('created_at', options.filters.dateTo + 'T23:59:59.999Z');
        }
      }

      // Filter by city
      if (options.filters.cities && options.filters.cities.length > 0) {
        query = applyInFilter(query, 'city', options.filters.cities);
      }

      // Filter by province
      if (options.filters.provinces && options.filters.provinces.length > 0) {
        query = applyInFilter(query, 'province', options.filters.provinces);
      }

      // Filter by region
      if (options.filters.regions && options.filters.regions.length > 0) {
        query = applyInFilter(query, 'region', options.filters.regions);
      }

      // Filter by newsletter subscription
      if (options.filters.newsletterSubscribed !== undefined) {
        if (options.filters.newsletterSubscribed) {
          query = query.eq('newsletter_subscribed', true);
        } else {
          query = query.or('newsletter_subscribed.is.null,newsletter_subscribed.eq.false');
        }
      }

      const hasPostFilters = (options.filters.interestIds && options.filters.interestIds.length > 0) ||
        (options.filters.tagIds && options.filters.tagIds.length > 0);

      // If no post-query filters needed, use the exact count from Supabase header
      if (!hasPostFilters) {
        const { count: exactCount, error } = await query;
        if (error) throw error;
        return exactCount || 0;
      }

      // With post-query filters, we need all rows - paginate to bypass server max-rows limit
      const buildQuery = () => {
        let q = supabase
          .from('clients')
          .select('id, stato_cliente, country')
          .eq('organization_id', options.organizationId);

        if (options.type === 'clients') q = q.in('stato_cliente', clientStateIds);
        else if (options.type === 'leads') q = q.in('stato_cliente', leadStateIds);

        if (options.filters.countries?.length) q = q.in('country', options.filters.countries);
        if (options.filters.userIds?.length) q = q.in(options.filters.userFilterField || 'assigned_to', options.filters.userIds);
        if (options.filters.entityTypes?.length) q = q.in('entity_type', options.filters.entityTypes);
        if (options.filters.clientStateIds?.length) q = q.in('stato_cliente', options.filters.clientStateIds);
        if (options.filters.leadStageIds?.length) {
          q = q.or(`lead_stage.in.(${options.filters.leadStageIds.join(',')}),pipeline_stage_id.in.(${options.filters.leadStageIds.join(',')})`);
        }
        if (options.filters.leadSourceIds?.length) q = q.in('lead_source', options.filters.leadSourceIds);
        if (options.filters.categories?.length) q = q.in('client_category', options.filters.categories);
        if (options.filters.dateFrom) q = q.gte('created_at', options.filters.dateFrom);
        if (options.filters.dateTo) q = q.lte('created_at', options.filters.dateTo + 'T23:59:59.999Z');
        if (options.filters.cities?.length) q = q.in('city', options.filters.cities);
        if (options.filters.provinces?.length) q = q.in('province', options.filters.provinces);
        if (options.filters.regions?.length) q = q.in('region', options.filters.regions);
        if (options.filters.newsletterSubscribed !== undefined) {
          if (options.filters.newsletterSubscribed) q = q.eq('newsletter_subscribed', true);
          else q = q.or('newsletter_subscribed.is.null,newsletter_subscribed.eq.false');
        }

        return q;
      };

      let filteredData = await fetchAllPages<{ id: string; stato_cliente: string; country: string }>(buildQuery);

      // Apply interest filters (post-query)
      if (options.filters.interestIds && options.filters.interestIds.length > 0) {
        const clientIds = filteredData.map(c => c.id);
        if (clientIds.length > 0) {
          const interestFilterIds = options.filters.interestIds;
          const interests = await queryInBatches<{ client_id: string; interest_category_id: string }>(
            clientIds,
            (batchIds) => supabase
              .from('client_interests')
              .select('client_id, interest_category_id')
              .in('client_id', batchIds)
              .in('interest_category_id', interestFilterIds)
          );

          const clientInterestsMap = new Map<string, Set<string>>();
          interests?.forEach(i => {
            if (!clientInterestsMap.has(i.client_id)) {
              clientInterestsMap.set(i.client_id, new Set());
            }
            clientInterestsMap.get(i.client_id)!.add(i.interest_category_id);
          });

          if (options.filterLogic === 'and') {
            filteredData = filteredData.filter(c => {
              const clientInterests = clientInterestsMap.get(c.id);
              if (!clientInterests) return false;
              return options.filters.interestIds!.every(id => clientInterests.has(id));
            });
          } else if (options.filterLogic === 'not') {
            filteredData = filteredData.filter(c => !clientInterestsMap.has(c.id));
          } else {
            filteredData = filteredData.filter(c => clientInterestsMap.has(c.id));
          }
        }
      }

      // Apply tag filters (post-query)
      if (options.filters.tagIds && options.filters.tagIds.length > 0) {
        const clientIds = filteredData.map(c => c.id);
        if (clientIds.length > 0) {
          const tagFilterIds = options.filters.tagIds;
          const tagAssignments = await queryInBatches<{ client_id: string; tag_id: string }>(
            clientIds,
            (batchIds) => supabase
              .from('client_tag_assignments')
              .select('client_id, tag_id')
              .in('client_id', batchIds)
              .in('tag_id', tagFilterIds)
          );

          const clientTagsMap = new Map<string, Set<string>>();
          tagAssignments?.forEach(t => {
            if (!clientTagsMap.has(t.client_id)) {
              clientTagsMap.set(t.client_id, new Set());
            }
            clientTagsMap.get(t.client_id)!.add(t.tag_id);
          });

          if (options.filterLogic === 'and') {
            filteredData = filteredData.filter(c => {
              const clientTags = clientTagsMap.get(c.id);
              if (!clientTags) return false;
              return options.filters.tagIds!.every(id => clientTags.has(id));
            });
          } else if (options.filterLogic === 'not') {
            filteredData = filteredData.filter(c => !clientTagsMap.has(c.id));
          } else {
            filteredData = filteredData.filter(c => clientTagsMap.has(c.id));
          }
        }
      }

      return filteredData.length;
    } catch (error) {
      console.error('Error counting exportable records:', error);
      throw error;
    }
  }

  /**
   * Export clients/leads with advanced filters and contacts
   */
  async exportClientsWithFilters(options: {
    type: 'clients' | 'leads' | 'both';
    organizationId: string;
    filters: {
      interestIds?: string[];
      tagIds?: string[];
      countries?: string[];
      userIds?: string[];
      userFilterField?: 'assigned_to' | 'sales_rep_id';
      entityTypes?: string[];
      clientStateIds?: string[];
      leadStageIds?: string[];
      leadSourceIds?: string[];
      categories?: string[];
      referrerIds?: string[];
      dateFrom?: string;
      dateTo?: string;
      cities?: string[];
      provinces?: string[];
      regions?: string[];
      newsletterSubscribed?: boolean;
    };
    filterLogic: 'or' | 'and' | 'not';
    includeContacts: boolean;
    maxContacts: number;
    format: 'excel' | 'csv';
  }): Promise<void> {
    try {
      console.log('🚀 [EXPORT WITH FILTERS] Starting export with options:', JSON.stringify(options, null, 2));

      // Get client states
      const { data: clientStates } = await supabase
        .from('client_states')
        .select('id, name, is_lead')
        .eq('organization_id', options.organizationId);

      const clientStatesMap = new Map(clientStates?.map(s => [s.id, s]) || []);
      const leadStateIds = clientStates?.filter(s => s.is_lead).map(s => s.id) || [];
      const clientStateIds = clientStates?.filter(s => !s.is_lead).map(s => s.id) || [];

      // Build query function for pagination (server max-rows = 1000)
      const selectFields = `
          id,
          business_name,
          trade_name,
          entity_type,
          vat_number,
          tax_code,
          email,
          phone,
          whatsapp_number,
          street_address,
          street_number,
          city,
          postal_code,
          province,
          region,
          country,
          country_code,
          stato_cliente,
          lead_source,
          client_category,
          created_at,
          updated_at,
          assigned_to,
          sales_rep_id,
          referrer_id,
          lead_stage,
          pipeline_stage_id,
          newsletter_subscribed,
          newsletter_email,
          contacts:client_contacts!client_contacts_client_id_fkey(id, name, email, phone, role, area, is_primary)
        `;

      const isNot = options.filterLogic === 'not';
      const applyInFilter = (q: any, column: string, values: string[]) => {
        if (isNot) return q.not(column, 'in', `(${values.join(',')})`);
        return q.in(column, values);
      };

      const buildExportQuery = () => {
        let q = supabase
          .from('clients')
          .select(selectFields)
          .eq('organization_id', options.organizationId)
          .or(EXCLUDE_REFERENTI) // Fase 2: no persone-referente nell'export ('both' incluso)
          .order('created_at', { ascending: false });

        if (options.type === 'clients') q = q.in('stato_cliente', clientStateIds);
        else if (options.type === 'leads') q = q.in('stato_cliente', leadStateIds);

        if (options.filters.countries?.length) q = applyInFilter(q, 'country', options.filters.countries);
        if (options.filters.userIds?.length) q = applyInFilter(q, options.filters.userFilterField || 'assigned_to', options.filters.userIds);
        if (options.filters.entityTypes?.length) q = applyInFilter(q, 'entity_type', options.filters.entityTypes);
        if (options.filters.clientStateIds?.length) q = applyInFilter(q, 'stato_cliente', options.filters.clientStateIds);
        if (options.filters.leadStageIds?.length) {
          if (isNot) {
            q = q.not('lead_stage', 'in', `(${options.filters.leadStageIds.join(',')})`);
            q = q.not('pipeline_stage_id', 'in', `(${options.filters.leadStageIds.join(',')})`);
          } else {
            q = q.or(`lead_stage.in.(${options.filters.leadStageIds.join(',')}),pipeline_stage_id.in.(${options.filters.leadStageIds.join(',')})`);
          }
        }
        if (options.filters.leadSourceIds?.length) q = applyInFilter(q, 'lead_source', options.filters.leadSourceIds);
        if (options.filters.categories?.length) q = applyInFilter(q, 'client_category', options.filters.categories);
        if (options.filters.referrerIds?.length) q = applyInFilter(q, 'referrer_id', options.filters.referrerIds);
        if (options.filters.dateFrom) {
          if (isNot) q = q.lt('created_at', options.filters.dateFrom);
          else q = q.gte('created_at', options.filters.dateFrom);
        }
        if (options.filters.dateTo) {
          if (isNot) q = q.gt('created_at', options.filters.dateTo + 'T23:59:59.999Z');
          else q = q.lte('created_at', options.filters.dateTo + 'T23:59:59.999Z');
        }
        if (options.filters.cities?.length) q = applyInFilter(q, 'city', options.filters.cities);
        if (options.filters.provinces?.length) q = applyInFilter(q, 'province', options.filters.provinces);
        if (options.filters.regions?.length) q = applyInFilter(q, 'region', options.filters.regions);
        if (options.filters.newsletterSubscribed !== undefined) {
          if (options.filters.newsletterSubscribed) q = q.eq('newsletter_subscribed', true);
          else q = q.or('newsletter_subscribed.is.null,newsletter_subscribed.eq.false');
        }

        return q;
      };

      const data = await fetchAllPages<any>(buildExportQuery);

      let filteredData = data || [];
      console.log('📊 [EXPORT WITH FILTERS] Initial records:', filteredData.length);

      // Modello Contatti/Aziende first-class: la colonna "contacts" dell'export legge solo
      // client_contacts (vuota per i clienti nuovi). Fondere i referenti da client_relationships
      // così l'export include i contatti anche delle aziende create col nuovo modello.
      if (options.includeContacts && filteredData.length > 0) {
        const { fetchReferentiAsContacts, mergeContactsByEmail } = await import('../utils/fetchReferentiContacts');
        const companyIds = filteredData.map((c: any) => c.id).filter(Boolean);
        const refMap: Record<string, any[]> = {};
        for (let i = 0; i < companyIds.length; i += 200) {
          Object.assign(refMap, await fetchReferentiAsContacts(companyIds.slice(i, i + 200)));
        }
        for (const c of filteredData) {
          c.contacts = mergeContactsByEmail((c.contacts ?? []) as any, refMap[c.id] ?? []);
        }
      }

      // Get all client IDs for filtering
      const allClientIds = filteredData.map(c => c.id);

      // Fetch interests for filtering
      let clientInterestsMap = new Map<string, Set<string>>();
      let clientInterestNamesMap = new Map<string, string[]>();
      if (allClientIds.length > 0) {
        const interests = await queryInBatches<any>(
          allClientIds,
          (batchIds) => supabase
            .from('client_interests')
            .select('client_id, interest_category_id, interest_category:interest_categories(id, name)')
            .in('client_id', batchIds)
        );

        interests?.forEach((i: any) => {
          if (!clientInterestsMap.has(i.client_id)) {
            clientInterestsMap.set(i.client_id, new Set());
            clientInterestNamesMap.set(i.client_id, []);
          }
          clientInterestsMap.get(i.client_id)!.add(i.interest_category_id);
          if (i.interest_category?.name) {
            clientInterestNamesMap.get(i.client_id)!.push(i.interest_category.name);
          }
        });
      }

      // Fetch tags for filtering
      let clientTagsMap = new Map<string, Set<string>>();
      let clientTagNamesMap = new Map<string, string[]>();
      if (allClientIds.length > 0) {
        const orgId = options.organizationId;
        const tagAssignments = await queryInBatches<any>(
          allClientIds,
          (batchIds) => supabase
            .from('client_tag_assignments')
            .select('client_id, tag_id, client_tags!inner(id, name)')
            .in('client_id', batchIds)
            .eq('organization_id', orgId)
            .eq('client_tags.is_active', true)
        );

        tagAssignments?.forEach((t: any) => {
          if (!clientTagsMap.has(t.client_id)) {
            clientTagsMap.set(t.client_id, new Set());
            clientTagNamesMap.set(t.client_id, []);
          }
          clientTagsMap.get(t.client_id)!.add(t.tag_id);
          if (t.client_tags?.name) {
            clientTagNamesMap.get(t.client_id)!.push(t.client_tags.name);
          }
        });
      }

      // Fetch assigned users
      const assignedUserIds = [...new Set(filteredData.map(c => c.assigned_to).filter(Boolean))];
      let assignedUsersMap = new Map();
      if (assignedUserIds.length > 0) {
        const { data: users } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .in('id', assignedUserIds);

        users?.forEach(u => assignedUsersMap.set(u.id, u));
      }

      // Fetch sales rep users
      const salesRepIds = [...new Set(filteredData.map(c => c.sales_rep_id).filter(Boolean))];
      let salesRepUsersMap = new Map();
      if (salesRepIds.length > 0) {
        const { data: salesRepUsers } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .in('id', salesRepIds);

        salesRepUsers?.forEach(u => salesRepUsersMap.set(u.id, u));
      }

      // Fetch referrers (segnalatori)
      const refIds = [...new Set(filteredData.map(c => c.referrer_id).filter(Boolean))];
      let referrersMap = new Map();
      if (refIds.length > 0) {
        const { data: referrersData } = await supabase
          .from('referrers')
          .select('id, full_name, email')
          .in('id', refIds);

        referrersData?.forEach(r => referrersMap.set(r.id, r));
      }

      // Fetch pipeline stages and pipeline names
      const pipelineStageIds = [...new Set(filteredData.map(c => c.pipeline_stage_id).filter(Boolean))];
      let pipelineStagesMap = new Map();
      let pipelineNamesMap = new Map();
      if (pipelineStageIds.length > 0) {
        const { data: stages } = await supabase
          .from('lead_pipeline_stages')
          .select('id, name, pipeline_id')
          .in('id', pipelineStageIds);

        if (stages) {
          stages.forEach(s => pipelineStagesMap.set(s.id, s));
          const pipelineIds = [...new Set(stages.map(s => s.pipeline_id).filter(Boolean))];
          if (pipelineIds.length > 0) {
            const { data: pipelines } = await supabase
              .from('lead_pipelines')
              .select('id, name')
              .in('id', pipelineIds);
            pipelines?.forEach(p => pipelineNamesMap.set(p.id, p.name));
          }
        }
      }

      // Apply interest filters
      if (options.filters.interestIds && options.filters.interestIds.length > 0) {
        if (options.filterLogic === 'and') {
          filteredData = filteredData.filter(c => {
            const clientInterests = clientInterestsMap.get(c.id);
            if (!clientInterests) return false;
            return options.filters.interestIds!.every(id => clientInterests.has(id));
          });
        } else if (options.filterLogic === 'not') {
          filteredData = filteredData.filter(c => {
            const clientInterests = clientInterestsMap.get(c.id);
            if (!clientInterests) return true;
            return !options.filters.interestIds!.some(id => clientInterests.has(id));
          });
        } else {
          filteredData = filteredData.filter(c => {
            const clientInterests = clientInterestsMap.get(c.id);
            if (!clientInterests) return false;
            return options.filters.interestIds!.some(id => clientInterests.has(id));
          });
        }
        console.log('📊 [EXPORT WITH FILTERS] After interest filter:', filteredData.length);
      }

      // Apply tag filters
      if (options.filters.tagIds && options.filters.tagIds.length > 0) {
        if (options.filterLogic === 'and') {
          filteredData = filteredData.filter(c => {
            const clientTags = clientTagsMap.get(c.id);
            if (!clientTags) return false;
            return options.filters.tagIds!.every(id => clientTags.has(id));
          });
        } else if (options.filterLogic === 'not') {
          filteredData = filteredData.filter(c => {
            const clientTags = clientTagsMap.get(c.id);
            if (!clientTags) return true;
            return !options.filters.tagIds!.some(id => clientTags.has(id));
          });
        } else {
          filteredData = filteredData.filter(c => {
            const clientTags = clientTagsMap.get(c.id);
            if (!clientTags) return false;
            return options.filters.tagIds!.some(id => clientTags.has(id));
          });
        }
        console.log('📊 [EXPORT WITH FILTERS] After tag filter:', filteredData.length);
      }

      // Build export data
      const exportData = filteredData.map((client: any, index) => {
        const assignedUser = client.assigned_to ? assignedUsersMap.get(client.assigned_to) : null;
        const salesRepUser = client.sales_rep_id ? salesRepUsersMap.get(client.sales_rep_id) : null;
        const referrer = client.referrer_id ? referrersMap.get(client.referrer_id) : null;
        const pipelineStage = client.pipeline_stage_id ? pipelineStagesMap.get(client.pipeline_stage_id) : null;
        const pipelineName = pipelineStage?.pipeline_id ? pipelineNamesMap.get(pipelineStage.pipeline_id) : '';
        const clientState = clientStatesMap.get(client.stato_cliente);
        const interestNames = clientInterestNamesMap.get(client.id) || [];
        const tagNames = clientTagNamesMap.get(client.id) || [];

        const baseData: Record<string, any> = {
          '#': index + 1,
          'Ragione Sociale': client.business_name || '',
          'Nome Insegna': client.trade_name || '',
          'Tipo': options.type === 'both' ? (clientState?.is_lead ? 'Lead' : 'Cliente') : '',
          'Tipo Entità': client.entity_type === 'individual' ? 'Persona Fisica' : 'Azienda',
          'Partita IVA': client.vat_number || '',
          'Codice Fiscale': client.tax_code || '',
          'Email': client.email || '',
          'Telefono': client.phone || '',
          'WhatsApp': client.whatsapp_number || '',
          'Via': client.street_address || '',
          'Numero Civico': client.street_number || '',
          'Città': client.city || '',
          'CAP': client.postal_code || '',
          'Provincia': client.province || '',
          'Regione': regionDisplayName(client.region),
          'Paese': client.country || '',
          'Codice Paese': client.country_code || '',
          'Stato': clientState?.name || '',
          'Categoria': client.client_category || '',
          'Interessi': interestNames.join(', '),
          'Tag': tagNames.join(', '),
          'Newsletter Iscritto': client.newsletter_subscribed ? 'Sì' : 'No',
          'Email Newsletter': client.newsletter_email || '',
          'Assegnato a': assignedUser?.full_name || '',
          'Email Assegnato': assignedUser?.email || '',
          'Venditore assegnato': salesRepUser?.full_name || '',
          'Email Venditore': salesRepUser?.email || '',
          'Segnalatore': referrer?.full_name || '',
          'Email Segnalatore': referrer?.email || '',
          'Pipeline': pipelineName || '',
          'Stato Pipeline': pipelineStage?.name || '',
          'Data Creazione': this.formatDate(client.created_at),
          'Ultimo Aggiornamento': this.formatDate(client.updated_at),
        };

        // Remove 'Tipo' column if not exporting 'both'
        if (options.type !== 'both') {
          delete baseData['Tipo'];
        }

        // Add contacts columns if requested
        if (options.includeContacts && client.contacts) {
          // Sort contacts: primary first, then by name
          const sortedContacts = [...client.contacts]
            .sort((a: any, b: any) => {
              if (a.is_primary && !b.is_primary) return -1;
              if (!a.is_primary && b.is_primary) return 1;
              return (a.name || '').localeCompare(b.name || '');
            })
            .slice(0, options.maxContacts);

          for (let i = 0; i < options.maxContacts; i++) {
            const contact = sortedContacts[i];
            const prefix = `Contatto${i + 1}`;
            baseData[`${prefix}_Nome`] = contact?.name || '';
            baseData[`${prefix}_Email`] = contact?.email || '';
            baseData[`${prefix}_Telefono`] = contact?.phone || '';
            baseData[`${prefix}_Ruolo`] = contact?.role || '';
            baseData[`${prefix}_Area`] = this.translateContactArea(contact?.area);
            baseData[`${prefix}_Primario`] = contact?.is_primary ? 'Sì' : '';
          }
        }

        return baseData;
      });

      console.log('✅ [EXPORT WITH FILTERS] Export data ready:', exportData.length, 'records');

      // Generate file based on format
      const typeLabel = options.type === 'clients' ? 'clienti' : options.type === 'leads' ? 'leads' : 'clienti_leads';
      const filename = `${typeLabel}_export_${this.getDateString()}`;

      if (options.format === 'csv') {
        await this.generateAndDownloadCSV({
          data: exportData,
          filename: `${filename}.csv`,
          sheetName: 'Export'
        });
      } else {
        await this.generateAndDownloadExcel({
          data: exportData,
          filename: `${filename}.xlsx`,
          sheetName: 'Export'
        });
      }

    } catch (error) {
      console.error('Error exporting with filters:', error);
      throw new Error('Errore durante l\'esportazione');
    }
  }

  private translateContactArea(area?: string): string {
    const translations: Record<string, string> = {
      'amministrazione': 'Amministrazione',
      'legale_rappresentante': 'Legale Rappresentante',
      'commerciale': 'Commerciale',
      'tecnico': 'Tecnico',
      'altro': 'Altro'
    };
    return translations[area || ''] || area || '';
  }

  /**
   * Generate and download CSV file
   */
  private async generateAndDownloadCSV({ data, filename, sheetName }: ExportData): Promise<void> {
    try {
      if (!data || data.length === 0) {
        throw new Error('No data to export');
      }

      // Create worksheet and convert to CSV
      const worksheet = XLSX.utils.json_to_sheet(data);

      // Generate CSV with UTF-8 BOM for Excel compatibility
      const csvString = XLSX.utils.sheet_to_csv(worksheet, { FS: ';' }); // Use semicolon for Italian locale

      // Add UTF-8 BOM
      const BOM = '\uFEFF';
      const csvWithBOM = BOM + csvString;

      // Create blob and download
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error generating CSV file:', error);
      throw new Error('Errore durante la generazione del file CSV');
    }
  }
}

export const exportService = new ExportService();
export { ExportService };